/**
 * IncomingCallBanner — a SECOND call ringing while the user is already on one (call waiting).
 *
 * Deliberately not the full IncomingCallModal: that card owns the screen with a bell ringtone,
 * which would blast over a live conversation. This is a compact card above the call widget with a
 * quiet call-waiting beep, so the user can hear the person they're talking to while they decide.
 * Rendered by VoiceCallProvider alongside FloatingCallWidget — the controls for the call in
 * progress must stay reachable while this is up.
 *
 * No `dark:` variants anywhere, on purpose: the app has no Tailwind `darkMode` config, so every
 * `dark:` class follows the USER'S OS theme regardless of the app's own light surfaces — which is
 * what made earlier corner UI render bright text on a light page. Same rule as
 * WebDialerReceiveIndicator and SimpleRoutingEditor.
 */

import { useEffect } from 'react';
import { Briefcase, Phone, PhoneOff } from 'lucide-react';
import type { CallSummary } from '@/lib/twilioVoice';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { deriveIncomingLabels, incomingNotificationBody } from '@/components/IncomingCallModal';

interface IncomingCallBannerProps {
  call: CallSummary;
  onAnswer: () => void;
  onDecline: () => void;
}

/**
 * Call-waiting beep: two short pips every few seconds, ~20dB below the ringtone's level. The point
 * is to be noticeable without talking over the live call, so this is deliberately NOT the bell
 * cadence used for a first ring.
 * Plays on the DEFAULT output rather than the call's selected sink — a headset user hears it on
 * their speakers. Accepted: routing it would mean a second sink-bound element per ring for a tone
 * the user hears either way.
 */
function startWaitingBeep(): () => void {
  let ctx: AudioContext | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    ctx = new AudioContext();
    void ctx.resume().catch(() => { /* stays suspended without a prior user gesture */ });

    const pip = (at: number) => {
      if (!ctx) return;
      const gain = ctx.createGain();
      // Soft edges: a hard start/stop clicks.
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.04, at + 0.01);
      gain.gain.setValueAtTime(0.04, at + 0.07);
      gain.gain.linearRampToValueAtTime(0, at + 0.1);
      gain.connect(ctx.destination);

      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 440;
      osc.connect(gain);
      osc.start(at);
      osc.stop(at + 0.1);
    };

    const cadence = () => {
      if (ctx && ctx.state === 'running') {
        pip(ctx.currentTime);
        pip(ctx.currentTime + 0.25);
      }
      timer = setTimeout(cadence, 4000);
    };
    cadence();
  } catch { /* no audio — the banner and notification still alert */ }

  return () => {
    if (timer !== null) clearTimeout(timer);
    if (ctx) void ctx.close().catch(() => { /* ignore */ });
  };
}

export function IncomingCallBanner({ call, onAnswer, onDecline }: IncomingCallBannerProps) {
  const context = call.incomingContext;
  const labels = deriveIncomingLabels(call.remoteNumber ?? '', context);
  const { line, displayNumber, headline, subtitle, lineLabel, jobLabel } = labels;

  // Mount = start alerting, unmount = stop, so the beep exactly follows the ring.
  useEffect(() => startWaitingBeep(), []);

  // Browser notification, tagged per call so it sits alongside the first call's rather than
  // replacing it (a backgrounded tab is exactly when a waiting call is easiest to miss).
  useEffect(() => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    let notification: Notification | null = null;
    try {
      notification = new Notification(context?.contactName || 'Second incoming call', {
        body: incomingNotificationBody(labels, context),
        tag: `web-dialer-incoming-${call.callSid}`,
        requireInteraction: true,
      });
      notification.onclick = () => { window.focus(); notification?.close(); };
    } catch { /* some platforms throw for page-scoped notifications — ignore */ }
    return () => notification?.close();
    // Primitives only: the context/labels objects are rebuilt on every state emit, and re-running
    // this would tear down and re-pop a requireInteraction notification mid-ring.
  }, [call.callSid, displayNumber, line?.e164, lineLabel, context?.contactName, jobLabel]);

  return (
    <div className="fixed bottom-[28rem] right-6 z-[10000] w-80 rounded-2xl border border-amber-300 bg-white p-4 shadow-2xl">
      <div className="flex items-center gap-3">
        <div className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-30" />
          <Phone className="h-5 w-5 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Call waiting</p>
          <p className="truncate text-base font-semibold text-gray-900">{headline}</p>
          {subtitle && <p className="truncate text-xs text-gray-600">{subtitle}</p>}
          {/* Which line is ringing — the point of the card for anyone covering several numbers. */}
          {line && (
            <p className="text-xs text-gray-600">
              to {formatPhoneNumber(line.e164)}
              {lineLabel ? <span className="text-gray-600"> · {lineLabel}</span> : null}
            </p>
          )}
        </div>
      </div>
      {jobLabel && (
        <p className="mt-2 inline-flex max-w-full items-center gap-1.5 truncate rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
          <Briefcase className="h-3.5 w-3.5 flex-shrink-0" />
          {jobLabel}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <button
          onClick={onDecline}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
        >
          <PhoneOff className="h-4 w-4" />
          Decline
        </button>
        <button
          onClick={onAnswer}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-green-600 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700"
        >
          <Phone className="h-4 w-4" />
          Answer
        </button>
      </div>
      {/* Says what the Answer button will DO — the current call is not dropped, and finding that
          out by clicking is a bad way to learn it. */}
      <p className="mt-2 text-center text-[11px] text-gray-600">
        Answering puts your current call on hold
      </p>
    </div>
  );
}
